import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { Audit, Inventory } from "../../../../api";
import { useAuth, usePermissions } from "../../../../hooks";
import { ACTIONS } from "../../../../utils/constants";

import {
  CategoryBreakdown,
  DashboardMetrics,
  PeriodSummary,
  ProfitChart,
  TopProducts,
  UnitsSoldChart,
} from "../components";

import styles from "./Home.module.scss";
import {
  buildDashboardMetrics,
  buildExportFilename,
  buildTopProductsByStock,
  buildYearlyProfitSeries,
  buildYearlySalesCsv,
  downloadCsv,
  flattenExistences,
  getCategoryBreakdown,
  getExistencesResponse,
  getFirstName,
  getGreeting,
  getProfitSummaryResponse,
  getSeriesTotals,
  getSummaryResponse,
  getYearOptions,
  getYearlySalesDetailResponse,
} from "./Home.form";

const YEAR_OPTIONS = getYearOptions(new Date(), 5);

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAction } = usePermissions();
  const canExport = hasAction("dashboard", ACTIONS.EXPORT);

  const [year, setYear] = useState(new Date().getFullYear());

  const [metrics, setMetrics] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [profitSeries, setProfitSeries] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const inventoryController = new Inventory();

      const [summaryResult, existencesResult, profitResult] =
        await Promise.all([
          inventoryController.getSummary({ year }),
          inventoryController.getExistences(1, 100, {}),
          inventoryController.getProfitSummary({ year }),
        ]);

      const summary = getSummaryResponse(summaryResult);
      const existences = getExistencesResponse(existencesResult);
      const profitMonths = getProfitSummaryResponse(profitResult);
      const yearlySeries = buildYearlyProfitSeries(profitMonths, year);
      const periodTotals = getSeriesTotals(yearlySeries);

      const rows = flattenExistences(existences.docs || []);

      setMetrics(
        buildDashboardMetrics({
          summary,
          periodTotals,
          year,
        })
      );

      setTopProducts(buildTopProductsByStock(rows, 8));
      setProfitSeries(yearlySeries);
      setCategoryBreakdown(getCategoryBreakdown(summary));
    } catch (errorResponse) {
      console.error("loadDashboard error:", errorResponse);

      setMetrics(buildDashboardMetrics());
      setTopProducts([]);
      setProfitSeries([]);
      setCategoryBreakdown([]);

      setError(
        errorResponse?.msg ||
          "No fue posible cargar el resumen. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleViewProducts = () => {
    navigate("/admin/products");
  };

  const handleViewInventory = () => {
    navigate("/admin/inventory");
  };

  const handleYearChange = useCallback((event) => {
    setYear(Number(event.target.value));
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);
      setError("");

      const inventoryController = new Inventory();
      const result = await inventoryController.getYearlySalesDetail(year);
      const { items, totals } = getYearlySalesDetailResponse(result);

      downloadCsv(buildExportFilename(year), buildYearlySalesCsv(items, totals, year));

      new Audit()
        .recordExport(`Resumen de ventas ${year}`)
        .catch((auditError) => console.error("recordExport error:", auditError));
    } catch (errorResponse) {
      console.error("getYearlySalesDetail error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible exportar el resumen de este año."
      );
    } finally {
      setIsExporting(false);
    }
  }, [year]);

  const greeting = getGreeting();
  const firstName = getFirstName(user);

  return (
    <section className={styles.Home}>
      <header className={styles.PageHeader}>
        <div>
          <h1 className={styles.Greeting}>
            {greeting}
            {firstName ? `, ${firstName}` : ""}
          </h1>

          <p className={styles.GreetingSubtitle}>
            Resumen operativo de tu tienda.
          </p>
        </div>

        <button
          type="button"
          className={styles.QuickAction}
          onClick={handleViewInventory}
        >
          Ver inventario
          <Icon name="angle right" />
        </button>
      </header>

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      <div className={styles.PeriodFilterBar}>
        <label className={styles.YearControl}>
          <span>Año</span>

          <select value={year} onChange={handleYearChange}>
            {YEAR_OPTIONS.map((optionYear) => (
              <option key={optionYear} value={optionYear}>
                {optionYear}
              </option>
            ))}
          </select>
        </label>

        {canExport && (
          <button
            type="button"
            className={styles.ExportButton}
            onClick={handleExport}
            disabled={isLoading || isExporting}
          >
            <Icon name="download" />
            {isExporting ? "Exportando..." : "Exportar"}
          </button>
        )}
      </div>

      <DashboardMetrics metrics={metrics} isLoading={isLoading} />

      <PeriodSummary year={year} />

      <UnitsSoldChart data={profitSeries} isLoading={isLoading} />

      <ProfitChart data={profitSeries} isLoading={isLoading} />

      <TopProducts
        products={topProducts}
        isLoading={isLoading}
        onViewAll={handleViewProducts}
      />

      <CategoryBreakdown categories={categoryBreakdown} isLoading={isLoading} />
    </section>
  );
}
